import { Component } from '@theme/component';
import { debounce, onDocumentLoaded, setHeaderMenuStyle } from '@theme/utilities';
import { MegaMenuHoverEvent } from '@theme/events';

/**
 * A custom element that manages a header menu.
 *
 * @typedef {Object} State
 * @property {HTMLElement | null} activeItem - The currently active menu item.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} overflowMenu - The overflow menu.
 * @property {HTMLElement[]} [submenu] - The submenu in each respective menu item.
 *
 * @extends {Component<Refs>}
 */
class HeaderMenu extends Component {
  requiredRefs = ['overflowMenu'];

  /**
   * @type {MutationObserver | null}
   */
  #submenuMutationObserver = null;

  /**
   * Kez Rugs: hover-intent grace period. Horizon 3.4.0 deactivates on the first pointerleave,
   * which fires while the pointer crosses the gap between a menu item and its submenu panel,
   * so the panel collapses under the cursor. Deactivation now waits and is cancelled if the
   * pointer is still over the item or its panel (backport of the 4.x behaviour).
   * @type {ReturnType<typeof setTimeout> | undefined}
   */
  #deactivateTimer;
  static KEZ_DEACTIVATE_DELAY_MS = 220;

  /**
   * Kez Rugs: skim filter. While a panel is open, a sibling item only takes over after the pointer
   * dwells on it, so a diagonal move from an item into its panel does not switch menus on the way.
   * @type {ReturnType<typeof setTimeout> | undefined}
   */
  #activateTimer;
  /** @type {HTMLElement | null} */
  #pendingItem = null;
  static KEZ_ACTIVATE_DWELL_MS = 150;

  /** Kez Rugs: the menu item currently under the pointer (from the items' own enter/leave events). @type {HTMLElement | null} */
  #hoveredItem = null;
  /** Kez Rugs: whether the pointer is over an open submenu panel. */
  #hoveredSubmenu = false;

  /**
   * Kez Rugs: single source of truth for the pointer. Resolves the item or panel under the pointer
   * from the event target, opens immediately from idle, switches after a dwell, closes after a grace.
   * @param {PointerEvent} event
   */
  #onPointerMove = (event) => {
    if (event.pointerType && event.pointerType !== 'mouse') return;
    const target = event.target instanceof Element ? event.target : null;
    const listItem = target?.closest('.menu-list__list-item');
    const insideItem = listItem instanceof HTMLElement && this.contains(listItem) ? listItem : null;
    const panel = target?.closest('.menu-list__submenu');
    const insidePanel = panel instanceof HTMLElement && this.contains(panel) ? panel : null;
    const item = insideItem ? findMenuItem(insideItem) : null;

    if (item && insideItem) {
      this.#hoveredItem = item;
      this.#hoveredSubmenu = false;
      clearTimeout(this.#deactivateTimer);
    this.#deactivateTimer = undefined;
      if (item === this.#state.activeItem) {
        clearTimeout(this.#activateTimer);
        this.#pendingItem = null;
        return;
      }
      if (!this.#state.activeItem) {
        this.dispatchEvent(new MegaMenuHoverEvent());
        this.#activateItem(item, insideItem);
        return;
      }
      if (this.#pendingItem !== item) {
        clearTimeout(this.#activateTimer);
        this.#pendingItem = item;
        this.#activateTimer = setTimeout(() => {
          this.#pendingItem = null;
          if (this.#hoveredItem !== item) return;
          this.dispatchEvent(new MegaMenuHoverEvent());
          this.#activateItem(item, insideItem);
        }, HeaderMenu.KEZ_ACTIVATE_DWELL_MS);
      }
      return;
    }

    if (insidePanel) {
      this.#hoveredItem = null;
      this.#hoveredSubmenu = true;
      clearTimeout(this.#deactivateTimer);
    this.#deactivateTimer = undefined;
      clearTimeout(this.#activateTimer);
      this.#pendingItem = null;
      return;
    }

    // Outside items and panels
    this.#hoveredItem = null;
    this.#hoveredSubmenu = false;
    clearTimeout(this.#activateTimer);
    this.#pendingItem = null;
    if (this.#state.activeItem && !this.#deactivateTimer) {
      this.#scheduleDeactivate();
    }
  };

  /** Kez Rugs: close the active item unless the pointer is back on it or on its panel by the time the grace period ends. */
  #scheduleDeactivate = () => {
    clearTimeout(this.#deactivateTimer);
    this.#deactivateTimer = undefined;
    const leaving = this.#state.activeItem;
    if (!leaving) return;
    this.#deactivateTimer = setTimeout(() => {
      this.#deactivateTimer = undefined;
      if (this.#state.activeItem !== leaving) return;
      if (this.#hoveredItem === leaving || this.#hoveredSubmenu) return;
      this.#deactivate(leaving);
    }, HeaderMenu.KEZ_DEACTIVATE_DELAY_MS);
  };

  connectedCallback() {
    super.connectedCallback();

    onDocumentLoaded(this.#preloadImages);
    window.addEventListener('resize', this.#resizeListener);
    this.overflowMenu?.addEventListener('pointerleave', this.#overflowSubmenuListener);
    // Kez Rugs: pointer state comes from document-level pointermove (like Horizon 4.x). Boundary events
    // (pointerenter/leave) are reset by the mutations the menu makes while opening, so they fire a
    // "leave" toward the item's own link and close the panel under the cursor.
    document.addEventListener('pointermove', this.#onPointerMove, { passive: true });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    clearTimeout(this.#deactivateTimer);
    this.#deactivateTimer = undefined;
    clearTimeout(this.#activateTimer);
    window.removeEventListener('resize', this.#resizeListener);
    this.overflowMenu?.removeEventListener('pointerleave', this.#overflowSubmenuListener);
    document.removeEventListener('pointermove', this.#onPointerMove);
    this.#cleanupMutationObserver();
  }

  /**
   * Debounced resize event listener to recalculate menu style
   */
  #resizeListener = debounce(() => {
    setHeaderMenuStyle();
  }, 100);


  #overflowSubmenuListener = () => {
    // Kez Rugs: boundary events also fire on layout changes (resize, re-slotting), so close with the same grace check
    this.#scheduleDeactivate();
  };

  /**
   * @type {State}
   */
  #state = {
    activeItem: null,
  };

  /**
   * Get the overflow menu
   */
  get overflowMenu() {
    return /** @type {HTMLElement | null} */ (this.refs.overflowMenu?.shadowRoot?.querySelector('[part="overflow"]'));
  }

  /**
   * Whether the overflow list is hovered
   * @returns {boolean}
   */
  get overflowListHovered() {
    return this.refs.overflowMenu?.shadowRoot?.querySelector('[part="overflow-list"]')?.matches(':hover') ?? false;
  }

  get headerComponent() {
    return /** @type {HTMLElement | null} */ (this.closest('header-component'));
  }

  /**
   * Activate the selected menu item immediately
   * @param {PointerEvent | FocusEvent} event
   */
  activate = (event) => {
    // Kez Rugs: pointer activation is handled by #onPointerMove; this path is keyboard focus only
    if (event.type !== 'focus') return;
    if (!(event.target instanceof Element) || !this.headerComponent) return;
    clearTimeout(this.#deactivateTimer);
    this.#deactivateTimer = undefined;
    clearTimeout(this.#activateTimer);
    this.#pendingItem = null;
    this.dispatchEvent(new MegaMenuHoverEvent());

    let item = findMenuItem(event.target);

    if (!item || item == this.#state.activeItem) return;

    this.#activateItem(item, event.target);
  };

  /**
   * Kez Rugs: the original body of activate(), keyed on the resolved item so the skim filter can commit it later.
   * @param {HTMLElement} item
   * @param {Element} target
   */
  #activateItem = (item, target) => {
    if (!this.headerComponent) return;
    clearTimeout(this.#deactivateTimer);
    this.#deactivateTimer = undefined;
    const isDefaultSlot = target.slot === '';

    this.dataset.overflowExpanded = (!isDefaultSlot).toString();

    const previouslyActiveItem = this.#state.activeItem;

    if (previouslyActiveItem) {
      previouslyActiveItem.ariaExpanded = 'false';
      // Kez Rugs: with deferred deactivation the previous panel can keep data-active when moving straight to another item
      const previousSubmenu = findSubmenu(previouslyActiveItem);
      if (previousSubmenu && previousSubmenu !== findSubmenu(item)) {
        delete previousSubmenu.dataset.active;
      }
    }

    this.#state.activeItem = item;
    this.ariaExpanded = 'true';
    item.ariaExpanded = 'true';

    let submenu = findSubmenu(item);
    const hasSubmenu = Boolean(submenu);

    if (!hasSubmenu && !isDefaultSlot) {
      submenu = this.overflowMenu;
    }

    if (submenu) {
      // Mark submenu as active for content-visibility optimization
      submenu.dataset.active = '';

      // Cleanup any existing mutation observer from previous menu activations
      this.#cleanupMutationObserver();

      // Monitor DOM mutations to catch deferred content injection (from section hydration)
      this.#submenuMutationObserver = new MutationObserver(() => {
        requestAnimationFrame(() => {
          // Double requestAnimationFrame to ensure the height is properly calculated and not defaulting to the contain-intrinsic-size
          requestAnimationFrame(() => {
            if (submenu.offsetHeight > 0) {
              this.headerComponent?.style.setProperty('--submenu-height', `${submenu.offsetHeight}px`);
              this.#cleanupMutationObserver();
            }
          });
        });
      });
      this.#submenuMutationObserver.observe(submenu, {childList: true, subtree: true});

      // Auto-disconnect after 500ms to prevent memory leaks
      setTimeout(() => {
        this.#cleanupMutationObserver();
      }, 500);
    }

    let finalHeight = submenu?.offsetHeight || 0;

    // For overflow menu, the height needs to be either content of the submenu or the total height of the menu list links
    if (!isDefaultSlot) {
      const overflowListHeight = this.#getOverflowListLinksHeight();
      if (hasSubmenu) {
        /* Note: When the submenu is inside the overflow menu, its offsetHeight is not valid due to the lack of padding
         * we could add the padding variables to the submenu.offsetHeight, but measuring the overflowMenu.offsetHeight is just easier */
        const overflowHeight = this.overflowMenu?.offsetHeight || 0;
        finalHeight = Math.max(overflowHeight, overflowListHeight);
      } else {
        finalHeight = overflowListHeight;
      }
    }

    if (!submenu) {
      // If there is no content to open, don't try to open it
      finalHeight = 0;
    }

    this.headerComponent.style.setProperty('--submenu-height', `${finalHeight}px`);
    this.#setFullOpenHeaderHeight(finalHeight);
    this.style.setProperty('--submenu-opacity', '1');
  };

  /**
   * Deactivate the active item after a delay
   * @param {PointerEvent | FocusEvent} event
   */
  deactivate(event) {
    // Kez Rugs: pointer deactivation is handled by #onPointerMove; this path is keyboard blur only
    if (event.type !== 'blur') return;
    if (!(event.target instanceof Element)) return;

    const menu = findSubmenu(this.#state.activeItem);
    const isMovingWithinMenu = event.relatedTarget instanceof Node && menu?.contains(document.activeElement);
    const isMovingToSubmenu =
      event.relatedTarget instanceof Node && event.type === 'blur' && menu?.contains(event.relatedTarget);
    const isMovingToOverflowMenu =
      event.relatedTarget instanceof Node && event.relatedTarget.parentElement?.matches('[slot="overflow"]');

    if (isMovingWithinMenu || isMovingToOverflowMenu || isMovingToSubmenu) return;

    this.#deactivate();
  }

  /**
   * Deactivate the active item immediately
   * @param {HTMLElement | null} [item]
   */
  #deactivate = (item = this.#state.activeItem) => {
    if (!item || item != this.#state.activeItem) return;

    // Don't deactivate if the overflow menu or overflow list is still being hovered
    if (this.overflowListHovered || this.overflowMenu?.matches(':hover')) return;

    this.headerComponent?.style.setProperty('--submenu-height', '0px');
    this.#setFullOpenHeaderHeight(0);
    this.style.setProperty('--submenu-opacity', '0');
    this.dataset.overflowExpanded = 'false';

    const submenu = findSubmenu(item);

    this.#state.activeItem = null;
    this.ariaExpanded = 'false';
    item.ariaExpanded = 'false';

    // Remove active state from submenu after animation completes
    if (submenu) {
      delete submenu.dataset.active;
    }
  };

  #getOverflowListLinksHeight() {
    const slottedMenuLinks = this.overflowMenu?.querySelector('slot')?.assignedElements();
    if (!slottedMenuLinks) return this.overflowMenu?.offsetHeight || 0;

    /**
     * @param {(submenu: HTMLElement) => void} cb
     */
    const mapSubmenus = (cb) => {
      slottedMenuLinks.forEach((link) => {
        const submenu = /** @type {HTMLElement | null} */ (link.querySelector('[ref="submenu[]"]'));
        if (submenu) {
          cb(submenu);
        }
      });
    }

    mapSubmenus((submenu) => {
      submenu.style.setProperty('display', 'none');
    });
    const height = this.overflowMenu?.offsetHeight || 0;
    mapSubmenus((submenu) => {
      submenu.style.removeProperty('display');
    });
    return height;
  }

  /**
   * Calculate and set the full open header height. If the submenu is not open, the full open header height is 0.
   * @param {number} submenuHeight
   */
  #setFullOpenHeaderHeight(submenuHeight) {
    if (!this.headerComponent) return;

    const isOverlapSituation = this.headerComponent.hasAttribute('data-submenu-overlap-bottom-row');

    const headerVisibleHeight =
      isOverlapSituation && this.headerComponent.offsetHeight > 0
        ? /** @type {HTMLElement | null} */ (this.headerComponent.querySelector('.header__row--top'))?.offsetHeight ?? 0
        : this.headerComponent.offsetHeight;

    const nothingToOpen = submenuHeight === 0;
    const fullOpenHeaderHeight = nothingToOpen ? 0 : submenuHeight + (headerVisibleHeight ?? 0);

    this.headerComponent?.style.setProperty('--full-open-header-height', `${fullOpenHeaderHeight}px`);
  }

  /**
   * Preload images that are set to load lazily.
   */
  #preloadImages = () => {
    const images = this.querySelectorAll('img[loading="lazy"]');
    images?.forEach((image) => image.removeAttribute('loading'));
  };

  #cleanupMutationObserver() {
    this.#submenuMutationObserver?.disconnect();
    this.#submenuMutationObserver = null;
  }
}

if (!customElements.get('header-menu')) {
  customElements.define('header-menu', HeaderMenu);
}

/**
 * Find the closest menu item.
 * @param {Element | null | undefined} element
 * @returns {HTMLElement | null}
 */
function findMenuItem(element) {
  if (!(element instanceof Element)) return null;

  if (element?.matches('[slot="more"')) {
    // Select the first overflowing menu item when hovering over the "More" item
    return findMenuItem(element.parentElement?.querySelector('[slot="overflow"]'));
  }

  return element?.querySelector('[ref="menuitem"]');
}

/**
 * Find the closest submenu.
 * @param {Element | null | undefined} element
 * @returns {HTMLElement | null}
 */
function findSubmenu(element) {
  const submenu = element?.parentElement?.querySelector('[ref="submenu[]"]');
  return submenu instanceof HTMLElement ? submenu : null;
}
